"use client";

import { supabase } from "@/lib/supabase";
import { useCallback, useState } from "react";
import { toast } from "sonner";

export type ProjectFilePreview = {
  filename: string;
  url: string;
};

export type UseProjectFilePreviewReturn = {
  preview: ProjectFilePreview | null;
  previewingFileId: string | null;
  openPreview: (fileId: string, filename: string) => Promise<void>;
  closePreview: () => void;
};

// Resolves a knowledge-base file id to a short-lived signed storage URL with a
// read (inline) disposition — shared by the preview panel and file thumbnails.
export async function resolveProjectFileSignedUrl(fileId: string): Promise<string> {
  const { data: fileRecord, error: fetchError } = await supabase
    .from("files")
    .select("file_path")
    .eq("id", fileId)
    .single();
  if (fetchError || !fileRecord?.file_path) {
    throw new Error(fetchError?.message ?? "File path not found");
  }

  const { data: signedData, error: signError } = await supabase.storage
    .from("files")
    .createSignedUrl(fileRecord.file_path, 3600);
  if (signError || !signedData?.signedUrl) {
    throw new Error(signError?.message ?? "Could not create preview link");
  }

  return signedData.signedUrl;
}

// Resolves a knowledge-base file to a short-lived signed storage URL and holds
// it as the current target for the shared DocPreviewPanel — same experience as
// chat attachment previews, driven from project file lists.
export function useProjectFilePreview(): UseProjectFilePreviewReturn {
  const [preview, setPreview] = useState<ProjectFilePreview | null>(null);
  const [previewingFileId, setPreviewingFileId] = useState<string | null>(null);

  const openPreview = useCallback(async (fileId: string, filename: string) => {
    setPreviewingFileId(fileId);
    try {
      const url = await resolveProjectFileSignedUrl(fileId);
      setPreview({ filename, url });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load file preview.");
    } finally {
      setPreviewingFileId(null);
    }
  }, []);

  const closePreview = useCallback(() => setPreview(null), []);

  return { preview, previewingFileId, openPreview, closePreview };
}
